import { Link } from 'react-router-dom'
import SupportLayout from './SupportLayout'
import { CATEGORIES, getArticlesByCategory } from './articles'
import usePageTitle from '../../hooks/usePageTitle'

// ─── SupportHome ─────────────────────────────────────────────────────────────
//
// The /support index page. Lists every article grouped by category. Each
// article is a clickable card with title + summary. Drafts get a "Coming
// soon" badge so users know what's available now versus later.

export default function SupportHome() {
  usePageTitle('Support')
  return (
    <SupportLayout
      title="Support"
      subtitle="Guides for getting the most out of Receipt Chaser, plus a way to email us when you need a hand."
    >
      {/* Hero intro + contact CTA */}
      <div style={{
        background: 'var(--white)',
        border: '1px solid var(--surface-2)',
        borderRadius: 12,
        padding: 28,
        marginBottom: 32,
        display: 'flex',
        gap: 24,
        alignItems: 'center',
        flexWrap: 'wrap',
      }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <h2 style={{
            margin: '0 0 8px',
            fontSize: '1.2rem',
            lineHeight: 1.3,
          }}>
            Can't find what you need?
          </h2>
          <p style={{
            margin: 0,
            fontSize: '0.92rem',
            color: 'var(--ink-muted)',
            lineHeight: 1.6,
          }}>
            Real humans, no chatbots. We'll get back to you shortly.
          </p>
        </div>
        <Link
          to="/support/contact"
          className="btn btn-primary"
          style={{ padding: '10px 22px', fontSize: '0.92rem', whiteSpace: 'nowrap' }}
        >
          Contact support →
        </Link>
      </div>

      {/* Category sections */}
      {CATEGORIES.map(cat => {
        const articles = getArticlesByCategory(cat.id)
        if (articles.length === 0) return null
        return (
          <section key={cat.id} style={{ marginBottom: 40 }}>
            <div style={{ marginBottom: 16 }}>
              <h2 style={{
                margin: '0 0 4px',
                fontSize: '1.15rem',
                lineHeight: 1.3,
              }}>
                {cat.label}
              </h2>
              <p style={{
                margin: 0,
                fontSize: '0.9rem',
                color: 'var(--ink-muted)',
                lineHeight: 1.55,
              }}>
                {cat.blurb}
              </p>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: 12,
            }}>
              {articles.map(article => (
                <ArticleCard key={article.slug} article={article} />
              ))}
            </div>
          </section>
        )
      })}

      {/* Suggestion prompt at the bottom of the page */}
      <section style={{
        marginTop: 12,
        padding: '24px 28px',
        background: 'var(--white)',
        border: '1px dashed var(--surface-3)',
        borderRadius: 10,
        display: 'flex',
        gap: 24,
        alignItems: 'center',
        flexWrap: 'wrap',
      }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <h2 style={{
            margin: '0 0 6px',
            fontSize: '1.05rem',
            lineHeight: 1.3,
          }}>
            Got an idea for the product?
          </h2>
          <p style={{
            margin: 0,
            fontSize: '0.88rem',
            color: 'var(--ink-muted)',
            lineHeight: 1.6,
          }}>
            Suggestions from the people who use the product every day
            shape what we build next. Tell us what's missing or what
            would make your workflow faster.
          </p>
        </div>
        <Link
          to="/support/suggest"
          className="btn btn-secondary"
          style={{ padding: '9px 20px', fontSize: '0.9rem', whiteSpace: 'nowrap' }}
        >
          Suggest a feature →
        </Link>
      </section>
    </SupportLayout>
  )
}

// ─── Article card ────────────────────────────────────────────────────────────

function ArticleCard({ article }) {
  return (
    <Link
      to={`/support/${article.slug}`}
      style={{
        display: 'block',
        background: 'var(--white)',
        border: '1px solid var(--surface-2)',
        borderRadius: 10,
        padding: 18,
        textDecoration: 'none',
        transition: 'border-color 0.15s, transform 0.15s',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.borderColor = 'var(--accent)'
        e.currentTarget.style.transform = 'translateY(-1px)'
      }}
      onMouseLeave={e => {
        e.currentTarget.style.borderColor = 'var(--surface-2)'
        e.currentTarget.style.transform = 'translateY(0)'
      }}
    >
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 8,
        marginBottom: 6,
      }}>
        <h3 style={{
          margin: 0,
          fontSize: '0.98rem',
          fontWeight: 600,
          color: 'var(--ink)',
          lineHeight: 1.35,
        }}>
          {article.title}
        </h3>
        {article.draft && (
          <span style={{
            fontSize: '0.68rem',
            background: 'var(--surface-2)',
            color: 'var(--ink-muted)',
            padding: '2px 8px',
            borderRadius: 100,
            whiteSpace: 'nowrap',
            flexShrink: 0,
            fontWeight: 500,
          }}>
            Coming soon
          </span>
        )}
      </div>
      <p style={{
        margin: 0,
        fontSize: '0.86rem',
        color: 'var(--ink-muted)',
        lineHeight: 1.55,
      }}>
        {article.summary}
      </p>
    </Link>
  )
}
